/**
 * BuyCreditsDialog Component
 * Dialog for purchasing analysis credits with backend-driven pricing
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, Loader2, CheckCircle, AlertCircle, Copy, Check, Star } from 'lucide-react';
import {
  GlassModal,
  GlassModalContent,
  GlassModalHeader,
  GlassModalTitle,
  GlassModalDescription,
} from '@/components/common/GlassModal';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { CheckoutForm } from '@/components/credits/CheckoutForm';
import { useCreditPricing } from '@/hooks/useCredits';
import type { CreditPurchaseOrderResponse, DiscountTier } from '@/types/profile';

interface BuyCreditsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const PACKAGES = [
  { quantity: 1, labelKey: 'package1' },
  { quantity: 5, labelKey: 'package5' },
  { quantity: 10, labelKey: 'package10' },
  { quantity: 25, labelKey: 'package25' },
] as const;

type Step = 'selection' | 'checkout' | 'confirmation';

export function BuyCreditsDialog({ isOpen, onClose }: BuyCreditsDialogProps) {
  const { t } = useTranslation();
  const [selectedQuantity, setSelectedQuantity] = useState(10);
  const [step, setStep] = useState<Step>('selection');
  const [completedOrder, setCompletedOrder] = useState<CreditPurchaseOrderResponse | null>(null);
  const [copiedOrderId, setCopiedOrderId] = useState(false);

  const { data: pricing, isLoading: pricingLoading, isError: pricingError } = useCreditPricing();

  const minQty = pricing?.minimumQuantity ?? 1;
  const maxQty = pricing?.maximumQuantity ?? 100;
  const currency = pricing?.currency ?? 'EUR';
  const basePricePerCredit = pricing?.pricePerCredit ?? 95;
  const tiers: DiscountTier[] = pricing?.discountTiers ?? [];

  // Reset step when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setStep('selection');
      setCompletedOrder(null);
      setCopiedOrderId(false);
    }
  }, [isOpen]);

  const getDiscountTier = useCallback(
    (qty: number): DiscountTier => {
      return tiers.find((t) => qty >= t.minQuantity) ?? { minQuantity: 1, pricePerCredit: basePricePerCredit, discountLabel: '', discountPercent: 0 };
    },
    [tiers, basePricePerCredit]
  );

  const tier = useMemo(() => getDiscountTier(selectedQuantity), [getDiscountTier, selectedQuantity]);
  const effectivePrice = tier.pricePerCredit;
  const totalPrice = Math.round(effectivePrice * selectedQuantity * 100) / 100;
  const fullPrice = basePricePerCredit * selectedQuantity;
  const savings = Math.round((fullPrice - totalPrice) * 100) / 100;
  const currencySymbol = currency === 'EUR' ? '€' : currency;

  const handlePackageClick = useCallback(
    (quantity: number) => {
      setSelectedQuantity(Math.max(minQty, Math.min(quantity, maxQty)));
    },
    [minQty, maxQty]
  );

  const handleSliderChange = useCallback((value: number[]) => {
    setSelectedQuantity(value[0]);
  }, []);

  const handleIncrement = useCallback(() => {
    setSelectedQuantity((prev) => Math.min(prev + 1, maxQty));
  }, [maxQty]);

  const handleDecrement = useCallback(() => {
    setSelectedQuantity((prev) => Math.max(prev - 1, minQty));
  }, [minQty]);

  const handlePurchase = useCallback(() => {
    setStep('checkout');
  }, []);

  const handleBack = useCallback(() => {
    setStep('selection');
  }, []);

  const handleOrderComplete = useCallback((order: CreditPurchaseOrderResponse) => {
    setCompletedOrder(order);
    setStep('confirmation');
  }, []);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getTitle = () => {
    if (step === 'confirmation') return t('credits.checkout.confirmationTitle');
    if (step === 'checkout') return t('credits.checkout.title');
    return t('credits.buyCredits');
  };

  const getDescription = () => {
    if (step === 'confirmation') return t('credits.checkout.confirmationDescription');
    if (step === 'checkout') return t('credits.checkout.description');
    return t('credits.description');
  };

  return (
    <GlassModal open={isOpen} onOpenChange={() => {}}>
      <GlassModalContent
        className="max-w-5xl w-[calc(100%-1rem)] sm:w-[90vw] max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-8"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onCloseClick={onClose}
      >
        <GlassModalHeader className="mb-1">
          <GlassModalTitle className="gradient-text text-xl sm:text-2xl">
            {getTitle()}
          </GlassModalTitle>
          <GlassModalDescription>{getDescription()}</GlassModalDescription>
        </GlassModalHeader>

        {step === 'selection' ? (
          pricingLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : pricingError ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-sm text-red-600">{t('credits.pricingError')}</p>
            </div>
          ) : (
            <>
              {/* Leistungskatalog */}
              <div className="mb-5">
                <p className="text-xs text-[var(--color-charcoal)]/70 leading-relaxed mb-2.5 sm:mb-3">
                  {t('newAnalysis.catalogIntro')}
                </p>
                <div className="mb-2.5 sm:mb-3 p-2 rounded-lg bg-gradient-to-r from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10 border border-[var(--color-primary-start)]/20">
                  <div className="flex items-start gap-2">
                    <Star className="h-4 w-4 text-[var(--color-primary-start)] shrink-0 mt-0.5 fill-[var(--color-primary-start)]" />
                    <span className="text-[13px] sm:text-sm font-semibold text-[var(--color-charcoal)] leading-snug">
                      {t('newAnalysis.catalogHighlight')}
                    </span>
                  </div>
                  <p className="text-[11px] sm:text-xs text-[var(--color-charcoal)]/55 mt-1 ml-6">
                    {t('newAnalysis.catalogAddon')}
                  </p>
                </div>

                <ul className="space-y-1.5 sm:space-y-2">
                  {([
                    { key: 'catalogMassnahmen', availability: 'both' as const },
                    { key: 'catalogProdukt', availability: 'both' as const },
                    { key: 'catalogKommunikation', availability: 'both' as const },
                    { key: 'catalogHandlungsempfehlungen', availability: 'both' as const },
                    { key: 'catalogVertriebsgebiet', availability: 'privatkunden' as const },
                    { key: 'catalogZielgruppeB2C', availability: 'privatkunden' as const },
                    { key: 'catalogSwot', availability: 'geschaeftskunden' as const },
                    { key: 'catalogZielgruppeB2B', availability: 'geschaeftskunden' as const },
                  ]).map(({ key, availability }) => {
                    const showTag = availability !== 'both';

                    return (
                      <li key={key} className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 mt-0.5 text-[var(--color-primary-start)]" />
                        <span className="text-[13px] sm:text-sm text-[var(--color-charcoal)]/80 leading-snug">
                          {t(`newAnalysis.${key}`)}
                          {showTag && (
                            <span className={`ml-1 sm:ml-1.5 inline-block text-[9px] sm:text-[10px] font-medium px-1.5 py-px sm:py-0.5 rounded-full align-middle ${
                              availability === 'privatkunden'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {t(`newAnalysis.catalogTag${availability === 'privatkunden' ? 'Privatkunden' : 'Geschaeftskunden'}`)}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Package Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-stretch">
                {PACKAGES.map((pkg) => {
                  const pkgTier = getDiscountTier(pkg.quantity);
                  const pkgEffectivePrice = pkgTier.pricePerCredit;
                  const pkgTotal = Math.round(pkgEffectivePrice * pkg.quantity * 100) / 100;
                  const pkgOriginalTotal = pkg.quantity * basePricePerCredit;
                  const hasDiscount = pkgTier.discountPercent > 0;
                  const isSelected = selectedQuantity === pkg.quantity;
                  const isPopular = pkg.quantity === 10;
                  const isBestValue = pkg.quantity === 25;
                  const hasGradientWrapper = isSelected || isPopular || isBestValue;

                  return (
                    <div
                      key={pkg.quantity}
                      className={`
                        rounded-[var(--radius-md)]
                        ${isSelected
                          ? 'p-[2px] gradient-bg shadow-md'
                          : isPopular
                            ? 'animated-gradient-border'
                            : isBestValue
                              ? 'p-[2px] bg-emerald-500'
                              : ''
                        }
                      `}
                    >
                      <button
                        type="button"
                        onClick={() => handlePackageClick(pkg.quantity)}
                        className={`
                          relative w-full h-full p-4 text-center cursor-pointer flex flex-col items-center justify-center
                          ${hasGradientWrapper
                            ? `rounded-[calc(var(--radius-md)-2px)] ${isSelected ? '!bg-white' : '!bg-[#f5f5f7]'}`
                            : 'rounded-[var(--radius-md)] bg-[#f5f5f7] border border-[#e5e5ea] hover:shadow-md'
                          }
                        `}
                      >
                        {isPopular && (
                          <span className="absolute -top-2.5 right-2 gradient-bg text-white text-[11px] font-semibold px-2 py-0.5 rounded-b-md">
                            {t('credits.mostPopular')}
                          </span>
                        )}
                        {isBestValue && (
                          <span className="absolute -top-2.5 right-2 bg-emerald-500 text-white text-[11px] font-semibold px-2 py-0.5 rounded-b-md">
                            {t('credits.bestValue')}
                          </span>
                        )}

                        <div className="text-xs font-medium uppercase tracking-wider text-[var(--color-charcoal)]/60 mb-1.5 mt-1">
                          {t(`credits.${pkg.labelKey}`)}
                        </div>

                        <div className="text-3xl font-bold text-[var(--color-charcoal)]">
                          {pkg.quantity}
                        </div>
                        <div className="text-xs text-[var(--color-charcoal)]/50 mb-3">
                          {pkg.quantity === 1 ? t('credits.request') : t('credits.requests')}
                        </div>

                        {hasDiscount && (
                          <div className="text-xs text-[var(--color-charcoal)]/50 line-through">
                            {formatCurrency(pkgOriginalTotal)} {currencySymbol}
                          </div>
                        )}
                        <div className="gradient-text text-xl font-bold">
                          {formatCurrency(pkgTotal)} {currencySymbol}
                        </div>

                        <div className="text-xs text-[var(--color-charcoal)]/60 mt-0.5">
                          {formatCurrency(pkgEffectivePrice)} {currencySymbol} {t('credits.perRequest')}
                        </div>

                        {hasDiscount ? (
                          <div className="mt-2 inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                            {pkgTier.discountLabel} {t('credits.discount')}
                          </div>
                        ) : (
                          <div className="mt-2.5 h-5" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Custom Amount Section */}
              <div className="mt-2 rounded-[var(--radius-lg)] border border-[#e5e5ea] bg-[#f5f5f7] p-5">
                <div className="text-sm font-medium text-[var(--color-charcoal)]/50 mb-4 text-center">
                  {t('credits.customAmount')}
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={handleDecrement}
                      disabled={selectedQuantity <= minQty}
                      className="h-9 w-9 rounded-full border border-[#e5e5ea] bg-white flex items-center justify-center text-[var(--color-charcoal)]/60 hover:bg-[#f0f0f2] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <div className="w-14 text-center">
                      <span className="text-2xl font-bold text-[var(--color-charcoal)]">
                        {selectedQuantity}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleIncrement}
                      disabled={selectedQuantity >= maxQty}
                      className="h-9 w-9 rounded-full border border-[#e5e5ea] bg-white flex items-center justify-center text-[var(--color-charcoal)]/60 hover:bg-[#f0f0f2] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  <Slider
                    value={[selectedQuantity]}
                    onValueChange={handleSliderChange}
                    min={minQty}
                    max={maxQty}
                    step={1}
                    className="flex-1"
                  />
                </div>

                <div className="flex flex-col gap-2 rounded-[var(--radius-md)] bg-white px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[var(--color-charcoal)]/60">
                      {selectedQuantity} × {formatCurrency(effectivePrice)} {currencySymbol}
                    </span>
                    <span className="gradient-text text-lg font-bold">
                      {formatCurrency(totalPrice)} {currencySymbol}
                    </span>
                  </div>
                  {savings > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-600 font-medium">
                        {tier.discountLabel} {t('credits.discount')}
                      </span>
                      <span className="text-emerald-600 font-medium">
                        {t('credits.youSave')} {formatCurrency(savings)} {currencySymbol}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary + CTA */}
              <div className="mt-1 flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1">
                  <div className="text-xs text-[var(--color-charcoal)]/60 uppercase tracking-wider">
                    {t('credits.totalPrice')}
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="gradient-text text-2xl sm:text-3xl font-bold leading-tight">
                      {formatCurrency(totalPrice)} {currencySymbol}
                    </span>
                    <span className="text-sm text-[var(--color-charcoal)]/50">
                      {selectedQuantity} {selectedQuantity === 1 ? t('credits.request') : t('credits.requests')}
                    </span>
                  </div>
                  {savings > 0 && (
                    <div className="text-sm text-emerald-600 font-medium mt-0.5">
                      {t('credits.youSave')} {formatCurrency(savings)} {currencySymbol}
                    </div>
                  )}
                </div>

                <Button
                  onClick={handlePurchase}
                  className="btn-primary w-full sm:w-auto text-base px-10 py-3 rounded-full"
                >
                  {t('credits.buyNow')}
                </Button>
              </div>
            </>
          )
        ) : step === 'checkout' ? (
          <CheckoutForm
            quantity={selectedQuantity}
            pricePerCredit={effectivePrice}
            currency={currency}
            taxRate={pricing?.taxRate ?? 0}
            onBack={handleBack}
            onOrderComplete={handleOrderComplete}
            basePricePerCredit={basePricePerCredit}
            discountLabel={tier.discountLabel}
          />
        ) : (
          /* Confirmation step */
          <div className="flex flex-col items-center text-center py-8 space-y-5 w-full">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[var(--color-charcoal)]">
              {t('credits.checkout.orderPlaced')}
            </h3>
            {completedOrder && (
              <div className="rounded-[var(--radius-lg)] border border-[#e5e5ea] bg-[#f5f5f7] p-6 w-full space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-[var(--color-charcoal)]/60 shrink-0 mr-4">{t('credits.checkout.orderId')}</span>
                  <span className="flex items-center gap-2">
                    <code className="font-mono text-xs font-medium bg-white px-2 py-1 rounded border border-[#e5e5ea]">
                      {completedOrder.orderNumber}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(completedOrder.orderNumber);
                        setCopiedOrderId(true);
                        setTimeout(() => setCopiedOrderId(false), 2000);
                      }}
                      className="text-[var(--color-charcoal)]/50 hover:text-[var(--color-charcoal)] transition-colors"
                      title={copiedOrderId ? t('credits.checkout.copied') : 'Copy'}
                    >
                      {copiedOrderId ? (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/60 shrink-0 mr-4">{t('credits.checkout.quantity')}</span>
                  <span className="font-medium">{completedOrder.quantity} {completedOrder.quantity === 1 ? t('credits.request') : t('credits.requests')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/60 shrink-0 mr-4">{t('credits.checkout.subtotalLabel')}</span>
                  <span className="font-medium whitespace-nowrap">{formatCurrency(completedOrder.subtotal)} {completedOrder.currency === 'EUR' ? '€' : completedOrder.currency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/60 shrink-0 mr-4">
                    {t('credits.checkout.vatLabel', { rate: String(Math.round(completedOrder.taxRate * 100)) })}
                  </span>
                  <span className="font-medium whitespace-nowrap">{formatCurrency(completedOrder.taxAmount)} {completedOrder.currency === 'EUR' ? '€' : completedOrder.currency}</span>
                </div>
                <div className="border-t border-[#e5e5ea] pt-3 flex justify-between">
                  <span className="font-semibold shrink-0 mr-4">{t('credits.checkout.grossPrice')}</span>
                  <span className="gradient-text font-bold text-lg whitespace-nowrap">{formatCurrency(completedOrder.total)} {completedOrder.currency === 'EUR' ? '€' : completedOrder.currency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-charcoal)]/60 shrink-0 mr-4">{t('credits.checkout.statusLabel')}</span>
                  <span className="font-medium">{t(`credits.checkout.status.${completedOrder.status}`)}</span>
                </div>
              </div>
            )}
            <p className="text-sm text-[var(--color-charcoal)]/60 leading-relaxed">
              {t('credits.checkout.invoiceSentNotice')}
            </p>
            <Button onClick={onClose} className="btn-primary px-8 py-3 rounded-full text-base mt-2">
              {t('credits.checkout.close')}
            </Button>
          </div>
        )}
      </GlassModalContent>
    </GlassModal>
  );
}
